// eslint-disable-next-line no-restricted-imports
import type Rollbar from 'rollbar';

import { getFullStory, IPublicConfig } from '@r-client/shared/util/core';

import {
  getIgnoredMessages,
  HOST_BLOCK_LIST,
  isIgnoredByFilename,
  isIgnoredByHost,
  shouldDowngradeToWarning,
} from './filters';
import { getErrorCodeFingerprint } from './fingerprint';
import { redactPayloadUrls } from './redact-payload-urls';

export type RollbarConfiguration = Rollbar.Configuration;
export interface IConfigurationSettings {
  /**
   * API integration token. if no token passed no reporting provided
   */
  accessToken: string | undefined;
  environment: IPublicConfig['applicationEnvironment'];
  codeVersion: string | undefined;
  serverHost: string;
  /**
   * kind is self control key not to miss configuration update on vendor change
   */
  kind: 'rollbar';
}

export const createReportingConfig = (
  settings: IConfigurationSettings
): Rollbar.Configuration => {
  // see https://docs.rollbar.com/docs/rollbarjs-configuration-reference
  return {
    accessToken: settings.accessToken,
    captureUncaught: true,
    captureUnhandledRejections: true,
    /** Filters items by `filename` in `body.trace_chain` or `body.trace` */
    hostBlockList: HOST_BLOCK_LIST,
    ignoredMessages: getIgnoredMessages(settings.environment),
    scrubFields: [
      'seedPhrase',
      'ssn',
      'taxId',
      'taxIdNumber',
      'phoneNumber',
      'accountNumber',
      'routingNumber',
      'mnemonic',
      'passphrase',
      'seedPhraseHint',
      'privateKey',
      'publicKey',
      'walletAddress',
      'contractAddress',
      'sensitive',
      // SEC Access Codes
      'cik',
      'ccc',
      'pmac',
      // Personal Information
      'firstName',
      'lastName',
      'email',
      'documentValue',
      'dateOfBirth',
      // Seed Phrase Components
      'firstRow',
      'secondRow',
      'thirdRow',
      'fourthRow',
    ],
    checkIgnore: (...args) => {
      return isIgnoredByHost(...args) || isIgnoredByFilename(...args);
    },
    payload: {
      environment: settings.environment,
      client: {
        javascript: {
          code_version: settings.codeVersion,
          source_map_enabled: true,
        },
      },
      server: {
        host: settings.serverHost,
      },
    },
    transform: (payload) => {
      // The page URL can carry secrets in its query string (for example the
      // new-device backup page: `?data=<JSON with email and backup share>`).
      // Redact the sensitive parameters and keep the rest of the URL.
      // `transform` runs after the SDK scrubber and after the telemetry is
      // added, so this is the one place that reaches every URL field.
      redactPayloadUrls(payload);

      // Always add FullStory URL to the payload
      const fs = getFullStory();
      const fullStoryUrl = fs?.getCurrentSessionURL(true);

      if (fullStoryUrl) {
        (payload as { fullStoryUrl: string }).fullStoryUrl = fullStoryUrl;
      }

      // Group by the SDK error code instead of the minified SDK frames.
      const fingerprint = getErrorCodeFingerprint(
        payload as Parameters<typeof getErrorCodeFingerprint>[0]
      );
      if (fingerprint) {
        (payload as { fingerprint: string }).fingerprint = fingerprint;
      }

      // Downgrade expected backend rejections so they stop ringing the
      // error inbox while remaining queryable at warning level.
      const typed = payload as {
        level?: string;
        body?: Record<string, unknown>;
      };
      const messageBody = typed.body?.['message'] as
        | { body?: string }
        | undefined;
      const traceException = (
        typed.body?.['trace'] as
          | { exception?: { message?: string } }
          | undefined
      )?.exception;
      const message = messageBody?.body ?? traceException?.message ?? '';

      if (message && shouldDowngradeToWarning(message)) {
        typed.level = 'warning';
      }
    },
  };
};
